import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { jobsApi, clientsApi, parksApi, contractorsApi } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  ArrowLeft, MapPin, User, Phone, Mail, Briefcase, 
  Calendar, FileText, Building2, Wrench, Clock,
  Share2, Download, ExternalLink
} from 'lucide-react';

const STAGE_COLORS = {
  'enquiry': 'bg-gray-100 text-gray-700',
  'quote_sent': 'bg-blue-100 text-blue-700',
  'quote_accepted': 'bg-green-100 text-green-700',
  'allocated_to_surveyor': 'bg-indigo-100 text-indigo-700',
  'survey_scheduled': 'bg-cyan-100 text-cyan-700',
  'survey_in_progress': 'bg-amber-100 text-amber-700',
  'survey_completed': 'bg-emerald-100 text-emerald-700',
};

const PRIORITY_COLORS = {
  'low': 'bg-gray-100 text-gray-600',
  'medium': 'bg-blue-100 text-blue-700',
  'high': 'bg-orange-100 text-orange-700',
  'urgent': 'bg-red-100 text-red-700',
};

export default function JobDetailPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [parks, setParks] = useState([]);

  useEffect(() => {
    loadData();
  }, [jobId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [jobRes, clientsRes, parksRes] = await Promise.all([
        jobsApi.getOne(jobId),
        clientsApi.getAll(),
        parksApi.getAll()
      ]);
      setJob(jobRes);
      setClients(clientsRes || []);
      setParks(parksRes || []);
    } catch (err) {
      console.error('Error loading job:', err);
      // Handle archived job (410 Gone) error
      if (err.response?.status === 410) {
        const errorData = err.response?.data?.detail;
        toast.error(errorData?.message || 'This job has been archived');
        setJob({ archived: true, message: errorData?.message || 'This job has been archived' });
      } else if (err.response?.status === 404) {
        toast.error('Job not found');
        setJob(null);
      } else {
        toast.error('Failed to load job details');
      }
    } finally {
      setLoading(false);
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.name || clientId || 'N/A';
  };

  const getParkName = (parkId) => {
    const park = parks.find(p => p.id === parkId);
    return park?.name || parkId || 'N/A';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-[#1F7A78] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="p-6 text-center">
        <p className="text-slate-500">Job not found</p>
        <Button onClick={() => navigate('/jobs')} className="mt-4">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Jobs
        </Button>
      </div>
    );
  }

  // Handle archived job state
  if (job.archived) {
    return (
      <div className="p-6 max-w-lg mx-auto text-center">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
          <div className="w-16 h-16 mx-auto mb-4 bg-amber-100 rounded-full flex items-center justify-center">
            <FileText className="h-8 w-8 text-amber-600" />
          </div>
          <h2 className="text-xl font-semibold text-amber-800 mb-2">Job Archived</h2>
          <p className="text-amber-700 mb-4">
            {job.message || 'This job has been archived during test data cleanup and is no longer accessible in the live system.'}
          </p>
          <Button onClick={() => navigate('/jobs')} className="bg-[#1F7A78] hover:bg-[#005456]">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Jobs
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/jobs')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-[#1F7A78]">{job.job_number}</h1>
              <Badge className={STAGE_COLORS[job.stage] || 'bg-gray-100'}>
                {job.stage?.replace('_', ' ')}
              </Badge>
              {job.priority && (
                <Badge className={PRIORITY_COLORS[job.priority?.toLowerCase()] || 'bg-gray-100'}>
                  {job.priority}
                </Badge>
              )}
            </div>
            <p className="text-slate-600">{job.title}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(`/jobs?edit=${job.id}`)}>
            Edit Job
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Property Details */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <MapPin className="h-4 w-4 text-[#1F7A78]" />
                Property Details
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-slate-500 text-xs">Park/Site</p>
                <p className="font-medium">{job.park_name || getParkName(job.park_id)}</p>
              </div>
              <div>
                <p className="text-slate-500 text-xs">Plot Number</p>
                <p className="font-medium">{job.plot_number || 'N/A'}</p>
              </div>
              <div>
                <p className="text-slate-500 text-xs">Address</p>
                <p className="font-medium">{job.property_address}</p>
              </div>
              <div>
                <p className="text-slate-500 text-xs">Postcode</p>
                <p className="font-medium">{job.property_postcode}</p>
              </div>
            </CardContent>
          </Card>

          {/* Job Details */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-[#1F7A78]" />
                Job Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-slate-500 text-xs">Job Type</p>
                  <p className="font-medium capitalize">{job.job_type?.replace('_', ' ')}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Client</p>
                  <p className="font-medium">{getClientName(job.client_id)}</p>
                </div>
                {job.damage_type && (
                  <div>
                    <p className="text-slate-500 text-xs">Damage Type</p>
                    <p className="font-medium capitalize">{job.damage_type?.replace('_', ' ')}</p>
                  </div>
                )}
                {job.scheduled_date && (
                  <div>
                    <p className="text-slate-500 text-xs">Scheduled Date</p>
                    <p className="font-medium">{format(new Date(job.scheduled_date), 'PPP')}</p>
                  </div>
                )}
              </div>
              {job.description && (
                <div>
                  <p className="text-slate-500 text-xs mb-1">Description</p>
                  <p className="bg-slate-50 p-3 rounded-lg">{job.description}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Policyholder Details */}
          {job.policyholder_name && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <User className="h-4 w-4 text-[#1F7A78]" />
                  Policyholder Details
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-slate-500 text-xs">Name</p>
                  <p className="font-medium">{job.policyholder_name}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Telephone</p>
                  <p className="font-medium">{job.policyholder_tel || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Email</p>
                  <p className="font-medium">{job.policyholder_email || 'N/A'}</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Insurance Details */}
          {job.insurer && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="h-4 w-4 text-[#1F7A78]" />
                  Insurance Details
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-slate-500 text-xs">Insurer</p>
                  <p className="font-medium capitalize">{job.insurer?.replace('_', ' ')}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Policy Number</p>
                  <p className="font-medium">{job.policy_number || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Claim Reference</p>
                  <p className="font-medium">{job.claim_reference || 'N/A'}</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Assignment */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Wrench className="h-4 w-4 text-[#1F7A78]" />
                Assignment
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              {job.assigned_contractor_name ? (
                <div className="bg-teal-50 p-3 rounded-lg border border-teal-200">
                  <p className="text-xs text-slate-500">Assigned Contractor</p>
                  <p className="font-semibold text-[#1F7A78]">{job.assigned_contractor_name}</p>
                </div>
              ) : job.assigned_surveyor_name ? (
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                  <p className="text-xs text-slate-500">Assigned Surveyor</p>
                  <p className="font-semibold text-blue-700">{job.assigned_surveyor_name}</p>
                </div>
              ) : (
                <div className="bg-slate-50 p-3 rounded-lg text-center">
                  <p className="text-slate-500">Not assigned</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Timeline */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="h-4 w-4 text-[#1F7A78]" />
                Timeline
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Created</span>
                <span>{job.created_at ? format(new Date(job.created_at), 'PPp') : 'N/A'}</span>
              </div>
              {job.quote_sent_at && (
                <div className="flex justify-between">
                  <span className="text-slate-500">Quote Sent</span>
                  <span>{format(new Date(job.quote_sent_at), 'PPp')}</span>
                </div>
              )}
              {job.quote_accepted_at && (
                <div className="flex justify-between">
                  <span className="text-slate-500">Quote Accepted</span>
                  <span>{format(new Date(job.quote_accepted_at), 'PPp')}</span>
                </div>
              )}
              {job.scheduled_date && (
                <div className="flex justify-between">
                  <span className="text-slate-500">Scheduled</span>
                  <span>{format(new Date(job.scheduled_date), 'PPp')}</span>
                </div>
              )}
              {job.survey_completed_at && (
                <div className="flex justify-between">
                  <span className="text-slate-500">Completed</span>
                  <span>{format(new Date(job.survey_completed_at), 'PPp')}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Files */}
          {job.files?.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Attached Files</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {job.files.map((file, idx) => (
                  <a 
                    key={idx}
                    href={file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-2 bg-slate-50 rounded hover:bg-slate-100 text-sm"
                  >
                    <FileText className="h-4 w-4 text-slate-400" />
                    <span className="truncate flex-1">{file.name || `File ${idx + 1}`}</span>
                    <ExternalLink className="h-3 w-3 text-slate-400" />
                  </a>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
